import { useEffect } from 'react';

/** Whether `el` scrolls, and can still move the way the wheel turns. */
const canScroll = (el: Element, dx: number, dy: number): boolean => {
  const style = getComputedStyle(el);
  const y = dy !== 0 && /auto|scroll/.test(style.overflowY)
    && (dy > 0 ? el.scrollTop + el.clientHeight < el.scrollHeight - 1 : el.scrollTop > 0);
  const x = dx !== 0 && /auto|scroll/.test(style.overflowX)
    && (dx > 0 ? el.scrollLeft + el.clientWidth < el.scrollWidth - 1 : el.scrollLeft > 0);
  return y || x;
};

/** The wheel scrolls a dialog's own scrolling area while that can still move, and
 *  nothing behind the dialog. The wheel with Ctrl held is a pinch, so zoom, and passes.
 *  A dialog that holds the page carries `data-modal`: selectors don't key on ARIA (JAR-2381). */
function holdWheel(e: WheelEvent) {
  if (e.ctrlKey) return;
  const target = e.target instanceof Element ? e.target : null;
  const dialog = target?.closest('[data-modal]');
  for (let el = target; el && dialog?.contains(el); el = el.parentElement) {
    if (canScroll(el, e.deltaX, e.deltaY)) return;
  }
  e.preventDefault();
}

/**
 * Holds the page still behind an open dialog when a mouse wheel turns (JAR-3017).
 * A touch screen hides the page's overflow instead (index.css). With a mouse that
 * hides the page's scrollbar and widens the page, and `scrollbar-gutter` doesn't
 * hold the width in WebKit, so the wheel is stopped rather than the page. Each
 * dialog adds its own listener, so closing one of two stacked dialogs leaves the
 * other's in place.
 */
export function useHoldPageScroll(active: boolean) {
  useEffect(() => {
    if (!active) return;
    const onWheel = (e: WheelEvent) => holdWheel(e);
    document.addEventListener('wheel', onWheel, { passive: false });
    return () => document.removeEventListener('wheel', onWheel);
  }, [active]);
}
